// src/pages/Onboarding/SlideShell.js
import React, { useState, useLayoutEffect } from 'react';
import ProgressBar from '../../components/Onboarding/ProgressBar';
import BackButton from '../../components/Onboarding/BackButton';
import PrimaryButton from '../../components/Onboarding/PrimaryButton';
import Logo from '../../components/Onboarding/Logo';
import PatientFileDeclaration from '../../components/Onboarding/PatientFileDeclaration';

// CONFIGURABLE ANIMATION CONSTANTS - Change these to control animation speed globally
const ANIMATION_DURATION = 300; // milliseconds

export default function SlideShell({
  title,
  subtitle,
  showBack = true,
  showLogo = false,
  onBack,
  onNext,
  nextLabel = 'Continue',
  currentStep,
  totalSteps,
  disabled = false,
  theme = 'light',
  animationDirection = 'forward',
  isTransitioning = false,
  children,
  privacyUrl,
  termsUrl,

  // NEW: control rendering of primary CTA
  showPrimary = true,

  // Optional helper text just beneath the primary button
  primaryNote = null,

  // Optional custom footer. If provided, overrides primary button.
  footer = null,

  // Patient File controls (lives in the footer, above PrimaryButton)
  showPatientFile = false,
  patientFileProps = {},

  // Legacy toggle; if true, hides primary even if showPrimary is true.
  hidePrimaryButton = false,

  // Layout controls
  scrollable = false,
  
  contentJustify = 'center', // 'center' | 'start'
  // Optional extra classes for the main content container (e.g., hide scrollbars)
  contentClassName = '',
  // NEW: Extra classes for the immediate content wrapper (applies in scrollable and non-scrollable modes)
  contentWrapperClassName = '',
  // NEW: Animate only the children inside the scroll container (not the whole scroll area)
  // Useful when you want some elements at the top of the scroll (like a local title)
  // to stay fixed in place relative to the scroll container during slide transitions.
  animateScrollableChildrenOnly = false,
  // NEW: Optional React node to render at the very top of the scrollable content
  // but OUTSIDE the animated transform when animateScrollableChildrenOnly is true.
  topScrollContent = null,

  // Primary button customization
  primaryVariant = 'solid',
  primaryButtonClassName = '',
  primaryButtonThemeOverride = null,
  primaryStartIcon = null,
  primaryEndIcon = null,
  primaryNoteClassName = '',
  // Optional content rendered just above the primary button
  abovePrimaryContent = null,

  // 🚀 NEW: Small subtext under the primary button
  showFooterSubtext = false,
  footerSubtextText = '',
  // (optional hook for future styling/icomponents)
  footerSubtextProps = {},
  // Optional per-slide background overrides
  backgroundClass,
  backgroundStyle,
  titleClassName = '',
  titleContainerClassName = '',
  titleContainerStyle = undefined,
  titleKey,
  // 🚀 NEW: footer mode + click handler
  footerMode = 'legal', // 'legal' | 'button'
  onFooterClick,
  // NEW: optional overlay rendered behind the title/subtitle area (per-slide customization)
  titleOverlay = null,
  // Prevent mobile keyboards from shrinking the layout when true
  lockToLargeViewport = false,
  // NEW: Freeze viewport-related CSS vars for this slide (treat keyboard as overlay)
  freezeViewportVars = false,
  // NEW: Hide the bottom h-24 spacer div (useful when you want content flush to bottom)
  hideBottomSpacer = false,
}) {
  const [slidePosition, setSlidePosition] = useState('center'); // 'left' | 'center' | 'right'
  const [contentKey, setContentKey] = useState(0); // Force re-render for animation
  const [frozenVars, setFrozenVars] = useState(null);

  // NEW: Patient File CTA interception
  const [pfTick, setPfTick] = useState(0);
  const [pfRunning, setPfRunning] = useState(false);
  const pfRunningRef = React.useRef(false);
  const pfFallbackTimerRef = React.useRef(null);

  // Handle slide position changes when transitioning (original logic)
  useLayoutEffect(() => {
    if (isTransitioning) {
      setContentKey((prev) => prev + 1);
      const startPosition = animationDirection === 'forward' ? 'right' : 'left';
      setSlidePosition(startPosition);
      const frame = requestAnimationFrame(() => {
        setSlidePosition('center');
      });
      return () => cancelAnimationFrame(frame);
    } else {
      setSlidePosition('center');
    }
  }, [isTransitioning, animationDirection]);

  const getTransformValue = () => {
    switch (slidePosition) {
      case 'left':
        return 'translateX(-100%)';
      case 'right':
        return 'translateX(100%)';
      case 'center':
      default:
        return 'translateX(0%)';
    }
  };

  const isDark = theme === 'dark';

  // Primary CTA handler with Patient File synchronization
  const handlePrimaryClick = () => {
    if (showPatientFile) {
      // If already running, ignore repeated clicks
      if (pfRunning) return;
      // Start declaration animation and delay next
      setPfRunning(true);
      setPfTick((t) => t + 1);
      // Safety: in rare cases onDone may never fire (e.g., very early click / animation race).
      // Auto-complete after a bounded timeout based on provided duration.
      const baseDuration = Number((patientFileProps && patientFileProps.durationMs) || 1500);
      // Add a small buffer so users can perceive the checkmark; clamp to sane bounds.
      const safetyMs = Math.min(6000, Math.max(800, Math.round(baseDuration + 800)));
      if (pfFallbackTimerRef.current) clearTimeout(pfFallbackTimerRef.current);
      pfFallbackTimerRef.current = setTimeout(() => {
        if (pfRunningRef.current) {
          handlePatientFileDone();
        }
      }, safetyMs);
      return; // do not call onNext yet; wait for onDone
    }
    onNext && onNext();
  };

  const handlePatientFileDone = () => {
    if (pfRunning) {
      setPfRunning(false);
      // Clear safety fallback if present
      if (pfFallbackTimerRef.current) {
        clearTimeout(pfFallbackTimerRef.current);
        pfFallbackTimerRef.current = null;
      }
      onNext && onNext();
    }
  };

  // Keep a ref in sync so our safety timer can check current state
  React.useEffect(() => {
    pfRunningRef.current = pfRunning;
  }, [pfRunning]);

  // Cleanup any pending safety timer on unmount
  React.useEffect(() => () => {
    if (pfFallbackTimerRef.current) {
      clearTimeout(pfFallbackTimerRef.current);
      pfFallbackTimerRef.current = null;
    }
  }, []);

  // Build container styles: when locked, use large viewport height with fallback
  React.useEffect(() => {
    if (!freezeViewportVars) return;
    try {
      const root = document.documentElement;
      const cs = getComputedStyle(root);
      const vh = cs.getPropertyValue('--app-viewport-height')?.trim() || '';
      const sb = cs.getPropertyValue('--app-safe-bottom')?.trim() || '';
      if (vh || sb) {
        setFrozenVars({ vh, sb });
      }
    } catch {}
  }, [freezeViewportVars]);

  const containerStyle = (() => {
    if (lockToLargeViewport) {
      return {
        ...(backgroundStyle || {}),
        ['--mm-lvh']: '100lvh',
        height: 'var(--mm-lvh, 100vh)',
        minHeight: 'var(--mm-lvh, 100vh)'
      };
    }
    if (freezeViewportVars && frozenVars) {
      return {
        ...(backgroundStyle || {}),
        ['--app-viewport-height']: frozenVars.vh,
        ['--app-safe-bottom']: frozenVars.sb,
      };
    }
    return backgroundStyle;
  })();

  return (
    <div
      className={`flex flex-col ${lockToLargeViewport ? '' : 'h-screen'} onboarding-shell relative ${
        backgroundClass ?? (isDark ? 'bg-mm_black' : 'bg-white')
      }`}
      style={containerStyle}
    >      <div className="w-full lg:w-2/5 mx-auto h-full flex flex-col">

        {/* Logo (optional) */}
        {showLogo && (
          <div className="flex justify-start px-4 pt-5 pb-3 sm:pt-6 sm:pb-4 relative z-20 md:mt-16">
            <Logo theme={theme} size="default" />
          </div>
        )}

        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 sm:py-4 relative z-20">
          <div className="w-8 h-8">
            {showBack && <BackButton onClick={onBack} />}
          </div>
          <div className="flex-1 flex justify-center px-4">
            {typeof currentStep === 'number' && typeof totalSteps === 'number' && (
              <ProgressBar current={currentStep} total={totalSteps} theme={theme} />
            )}
          </div>
          <div className="w-8 h-8" />
        </div>

        {/* Title / Subtitle */}
        {(title || subtitle) && (
          <div className={`px-5 sm:px-6 relative z-20 ${titleContainerClassName}`.trim()} style={titleContainerStyle}>
            {/* Optional per-slide overlay behind the title/subtitle */}
            {titleOverlay && (
              <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
                {titleOverlay}
              </div>
            )}
            {title && (
              <h1
                key={titleKey ?? title}
                className={`relative z-10 title-fade text-2xl font-bold leading-tight pb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                } ${titleClassName}`.trim()}
              >
                {title}
              </h1>
            )}
            {subtitle && (
              <p
                className={`relative z-10 title-fade text-sm leading-relaxed ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* MAIN CONTENT AREA (original animated wrapper) */}
        <div className="flex-1 relative overflow-hidden">
          {scrollable ? (
            animateScrollableChildrenOnly ? (
              // SCROLLABLE + PARTIAL ANIMATION: The scroll container is static;
              // only the inner children block transitions. This allows topScrollContent
              // to remain visually in place during slide transition while still scrolling together.
              <div className="absolute inset-0 flex flex-col">
                <div
                  className={`flex-1 px-5 sm:px-6 overflow-y-auto overscroll-contain min-h-0 ${contentClassName} ${contentWrapperClassName}`.trim()}
                  role="region"
                  aria-label="Slide content"
                  data-slide-shell-content="true"
                >
                  {topScrollContent}
                  <div
                    key={contentKey}
                    className="transition-transform ease-out"
                    style={{ transform: getTransformValue(), transitionDuration: `${ANIMATION_DURATION}ms` }}
                  >
                    <div className={contentJustify === 'center' ? 'flex flex-col justify-center' : ''}>{children}</div>
                    {/* spacer so last items don't hide behind footer */}
                    {!hideBottomSpacer && <div className="h-24" />}
                  </div>
                </div>
              </div>
            ) : (
              // ORIGINAL: The entire scroll container transitions
              <div
                key={contentKey}
                className="absolute inset-0 flex flex-col transition-transform ease-out"
                style={{ transform: getTransformValue(), transitionDuration: `${ANIMATION_DURATION}ms` }}
              >
                <div
                  className={`flex-1 px-5 sm:px-6 overflow-y-auto overscroll-contain min-h-0 ${contentClassName} ${contentWrapperClassName}`.trim()}
                  role="region"
                  aria-label="Slide content"
                  data-slide-shell-content="true"
                >
                  <div className={contentJustify === 'center' ? 'flex flex-col justify-center' : ''}>{children}</div>
                  {/* spacer so last items don't hide behind footer */}
                  {!hideBottomSpacer && <div className="h-24" />}
                </div>
              </div>
            )
          ) : (
            // NON-SCROLLABLE: content also participates in slide transform
            <div
              key={contentKey}
              className="absolute inset-0 flex flex-col transition-transform ease-out"
              style={{ transform: getTransformValue(), transitionDuration: `${ANIMATION_DURATION}ms` }}
            >
              <div
                className={`absolute inset-0 px-5 sm:px-6 flex flex-col ${
                  contentJustify === 'center' ? 'justify-center' : 'justify-start'
                } ${contentWrapperClassName}`.trim()}
                data-slide-shell-content="true"
              >
                {children}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
  <div className="px-5 pt-5 sm:px-6 sm:pt-6 onboarding-footer relative z-20">
          {footer ? (
            footer
          ) : (
            <div className="space-y-3">
              {showPatientFile && (
                <PatientFileDeclaration
                  theme={theme}
                  // Keep static until primary pressed (controlled mode)
                  animateNow={pfTick}
                  onDone={handlePatientFileDone}
                  {...patientFileProps}
                />
              )}

              {abovePrimaryContent}

              {showPrimary && !hidePrimaryButton && (
                <PrimaryButton
                  label={nextLabel}
                  onClick={handlePrimaryClick}
                  disabled={disabled || pfRunning}
                  theme={primaryButtonThemeOverride ?? theme}
                  variant={primaryVariant}
                  className={primaryButtonClassName}
                  startIcon={primaryStartIcon}
                  endIcon={primaryEndIcon}
                />
              )}

              {primaryNote && (
                <p className={`text-center text-xs ${primaryNoteClassName || (isDark ? 'text-gray-400' : 'text-gray-600')}`}>
                  {primaryNote}
                </p>
              )}

        {showFooterSubtext && (
          footerMode === 'legal' ? (
            <p
              className={`w-full text-center text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              {...(footerSubtextProps || {})}
            >
              {(footerSubtextText || 'By continuing, you agree to our ')}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`${isDark ? 'text-white' : 'text-gray-900'} font-semibold underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current`}
              >
                Privacy Policy
              </a>
              {' and '}
              <a
                href={termsUrl}
               target="_blank"
                rel="noopener noreferrer"
                className={`${isDark ? 'text-white' : 'text-gray-900'} font-semibold underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current`}
              >
                Terms &amp; Conditions
              </a>
              .
            </p>
          ) : (
            <button
              type="button"
              onClick={onFooterClick}
              className={`w-full text-center text-xs ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'} underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current`}
              {...(footerSubtextProps || {})}
            >
              {footerSubtextText}
            </button>
          )
        )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
